// PRD 17 §3.7/§3.8 — Notification Policy viewer (FEATURE_049). READ-ONLY per PRD
// v1.5/v1.8 + ADM.039: the fixed event catalog is shown in two sections — 환자 알림
// (app push) and 의료진 알림 (의료진 벨 + SMS) — each event's 제목·본문 (raw #변수 text),
// a 트리거·발송 note, and a read-only 상태 badge (켜짐/꺼짐). No toggle/edit/save —
// channel ON/OFF and copy are configured backend/설정-side. Grade R renders like any
// other row (no lock affordance; the always-send policy lives in PRD §3.8 prose).

import { useTranslation } from 'react-i18next';

import { usePermissionDeniedToast } from '@/components/feedback/use-permission-denied-toast';

import { useNotificationPoliciesQuery } from '../hooks/use-notification-settings';
import {
  NotificationSettingsApiError,
  type NotificationPolicy,
} from '../types/notification-settings.types';
import { splitByAudience } from '../utils/split-by-audience';

function TableSkeleton() {
  return (
    <div className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className="animate-pulse"
          style={{ height: 44, background: 'var(--muted)', borderRadius: 'var(--radius-sm)' }}
        />
      ))}
    </div>
  );
}

function LoadError({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useTranslation('notification-settings');
  return (
    <div
      role="alert"
      style={{
        padding: 16,
        border: '1px solid var(--destructive)',
        borderRadius: 'var(--radius-md)',
        background: '#FEE2E8',
        color: 'var(--destructive)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 16,
      }}
    >
      <span>{message}</span>
      <button type="button" className="btn btn-sm btn-outline" onClick={onRetry}>
        {t('states.retry')}
      </button>
    </div>
  );
}

function StatusBadge({ on, label }: { on: boolean; label: string }) {
  return (
    <span className={on ? 'badge badge-success' : 'badge badge-gray'} style={{ fontSize: 11, fontWeight: 600 }}>
      {label}
    </span>
  );
}

function ContentCell({ event }: { event: NotificationPolicy }) {
  const { t } = useTranslation('notification-settings');
  const hasCopy = Boolean(event.title && event.body);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {hasCopy ? (
        <>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{event.title}</div>
          {/* PRD 17 §3.8 — the 원문 is shown in FULL: FEATURE_049 makes this a read-only
              viewer of the exact push copy, so the body must never be height-capped or
              clipped (a hidden tail silently drops the [대시보드에서 확인] action line).
              pre-wrap (not pre-line) keeps the blank line the \n\n in the copy encodes. */}
          <div
            style={{
              fontSize: 12,
              color: 'var(--muted-foreground)',
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
            }}
          >
            {event.body}
          </div>
        </>
      ) : (
        <span style={{ color: 'var(--muted-foreground)', fontSize: 13 }}>{t('contentPreview.empty')}</span>
      )}
      {/* PRD 17 §3.7 — read-only 트리거·발송 note beneath the copy (BE-served). */}
      <span style={{ fontSize: 11, color: 'var(--muted-foreground)', marginTop: 2 }}>
        {t('content.triggerLabel')}: {event.trigger ?? '-'} · {t('content.timingLabel')}: {event.timing ?? '-'}
      </span>
    </div>
  );
}

// Resolves one channel column's ON/OFF badge for an event. The staff section's two columns read
// their own backend-served flag (PRD 17 §3.7: since v1.9 the bell and the SMS are independent — the
// two individual 09:00 events carry the bell copy while their SMS ships in the combined
// STAFF_DIGEST_SMS row, so a row is legitimately bell-on/SMS-off or bell-off/SMS-on). The patient
// section has one column and no staff channel flags (its channel is 앱 푸시 + 벨 with no automatic
// SMS), so it falls back to the event's own enabled flag.
function channelStateOf(event: NotificationPolicy, channelKey: string): boolean {
  if (channelKey === 'staffBell') {
    return event.bellEnabled ?? event.enabled;
  }
  if (channelKey === 'sms') {
    return event.smsEnabled ?? event.enabled;
  }
  return event.enabled;
}

// One section's table. `channelKeys` lists the status columns to render (patient → app push;
// staff → 의료진 벨 + SMS), each resolved through channelStateOf so the two staff channels can differ.
function PolicyTable({
  events,
  channelKeys,
  contentHeader,
  eventLabel,
}: {
  events: NotificationPolicy[];
  channelKeys: string[];
  contentHeader: string;
  eventLabel: (eventKey: string) => string;
}) {
  const { t } = useTranslation('notification-settings');
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <table className="table" style={{ width: '100%' }}>
        <thead>
          {/* SHCT-700 — headers stay on one line. The channel column's 96px, minus the 16px of
              padding `.table th` adds on each side, leaves 64px of text box, and "앱 푸시 + 벨" needs
              roughly 76px — so it broke at its last space and the header stood two lines tall while
              the shorter 의료진 벨 / SMS next to it did not. The table is table-layout: auto, so a
              width is only a hint: nowrap raises the column's min-content to the whole label and it
              widens to fit, which also holds for translations longer than the Korean. */}
          <tr>
            <th style={{ width: '30%', whiteSpace: 'nowrap' }}>{t('columns.item')}</th>
            <th style={{ whiteSpace: 'nowrap' }}>{contentHeader}</th>
            {channelKeys.map((ch) => (
              <th
                key={ch}
                style={{ width: 96, textAlign: 'center', whiteSpace: 'nowrap' }}
              >
                {t(`channels.${ch}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {events.map((event) => (
            <tr key={event.eventKey}>
              <td>
                {/* PRD 17 §3.7 — the item NAME stays on one line; only its 설명 below wraps. */}
                <div style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{eventLabel(event.eventKey)}</div>
                {/* PRD 17 §3.8 "표시 내용: 항목명·설명" — the item's read-only note under its name.
                    BE-served; null means the catalog has no note, so the line is omitted entirely. */}
                {event.description ? (
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 400,
                      color: 'var(--muted-foreground)',
                      whiteSpace: 'pre-line',
                      marginTop: 4,
                    }}
                  >
                    {event.description}
                  </div>
                ) : null}
                {event.dormant ? (
                  <span className="badge badge-gray" style={{ fontSize: 11, marginTop: 4 }}>
                    {t('dormantBadge')}
                  </span>
                ) : null}
              </td>
              <td>
                <ContentCell event={event} />
              </td>
              {channelKeys.map((ch) => {
                const on = channelStateOf(event, ch);
                return (
                  <td key={ch} style={{ textAlign: 'center' }}>
                    <StatusBadge on={on} label={on ? t('badge.on') : t('badge.off')} />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function NotificationSettingsView() {
  const { t } = useTranslation('notification-settings');
  // Event labels are served by the backend i18n catalog ('labels' namespace).
  const { t: tl } = useTranslation('labels');

  const { data, isLoading, error, refetch } = useNotificationPoliciesQuery();
  const denied =
    error instanceof NotificationSettingsApiError && error.code === 'INSUFFICIENT_PERMISSION';
  usePermissionDeniedToast(denied);

  if (isLoading) {
    return <TableSkeleton />;
  }
  // A GET that 403s despite the client-side READ gate (permission revoked mid-session) is
  // PRD 21 v2.14 §7.4 **P-4**: 서버 거부 → 토스트(error), 후속 동작 없음, 화면 유지.
  //
  // SHCT-582 — this used to be a modal that redirected home on acknowledge, which is P-1's
  // treatment. §7.4 separates the two deliberately: P-1 is an entry block and needs somewhere to
  // go back to, while P-4 is a momentary rejection of one request. Redirecting on a 403 also threw
  // away whatever else the operator had on screen. The screen now stays and shows its ordinary
  // load-error panel underneath the toast.
  if (denied) {
    return <LoadError message={t('states.loadError')} onRetry={() => void refetch()} />;
  }
  if (error || !data) {
    return <LoadError message={t('states.loadError')} onRetry={() => void refetch()} />;
  }
  if (data.length === 0) {
    return <div className="card" style={{ padding: 24, textAlign: 'center' }}>{t('states.empty')}</div>;
  }

  const eventLabel = (eventKey: string): string =>
    tl(`notificationPolicy.${eventKey}.label`, { defaultValue: eventKey });

  const { patientEvents, staffEvents } = splitByAudience(data);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <section>
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 12px' }}>{t('sections.patient')}</h2>
        <PolicyTable
          events={patientEvents}
          channelKeys={['appPushAndBell']}
          contentHeader={t('columns.content')}
          eventLabel={eventLabel}
        />
      </section>

      <section>
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 4px' }}>{t('sections.staff')}</h2>
        <p style={{ fontSize: 12, color: 'var(--muted-foreground)', margin: '0 0 12px' }}>
          {t('sections.staffRecipients')}
        </p>
        <PolicyTable
          events={staffEvents}
          channelKeys={['staffBell', 'sms']}
          contentHeader={t('columns.contentStaff')}
          eventLabel={eventLabel}
        />
      </section>
    </div>
  );
}
